import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "d-flex justify-content-between w-100 px-5 h-40" }
const _hoisted_2 = {
  key: 0,
  class: "loading"
}
const _hoisted_3 = {
  key: 1,
  class: "loading"
}
const _hoisted_4 = { class: "not-searched" }
const _hoisted_5 = {
  id: "select-time-date",
  class: "select-time-list select-time-date-list d-flex flex-column"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = {
  id: "select-time-hour",
  class: "select-time-list select-time-hour-list d-flex flex-column"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  id: "select-time-minute",
  class: "select-time-list select-time-minute-list d-flex flex-column"
}
const _hoisted_10 = ["onClick"]

import ValidationErrors from "@/components/Common/ValidationErrors.vue";
import { formatDate } from "@/services/functions";
import { ref, Ref, computed, nextTick, watch } from "vue";
import i18n from '@/lang/i18n';

type rangeType = {
  start_at: Date;
  end_at: Date;
};

type selectable = {
  value: string;
  selectable: boolean;
  selected: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectTime',
  props: {
    label: {},
    errors: {},
    modelValue: {},
    ranges: {},
    loading: { type: Boolean },
    reading: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;
const diffHour = new Date().getTimezoneOffset() / 60;

const globalRange = computed((): { start_at?: Date, end_at?: Date } => {
  if (!props.ranges.length) return { start_at: undefined, end_at: undefined };

  return {
    start_at: setStartOfDay(props.ranges[0].start_at),
    end_at: setEndOfDay(props.ranges[props.ranges.length - 1].end_at)
  };
})

const selectedDate: Ref<selectable["value"] | Date | undefined> = ref();
const selectedHour: Ref<selectable["value"] | Date | undefined> = ref();
const selectedMinute: Ref<selectable["value"] | Date | undefined> = ref();

const dates = computed(() => {
  if (!props.ranges.length) return []
  const start = globalRange.value.start_at;
  const end =  globalRange.value.end_at;

  return createDateList(start, end);
});

const hours = computed(() => {
  return createHourList();
});

const minutes = computed(() => {
  return createMinuteList();
});

watch(() => props.ranges, () => {
  if (!props.ranges[0]) {
    selectedDate.value = undefined;
    selectedHour.value = undefined;
    selectedMinute.value = undefined;
    return;
  }
})

function setStartOfDay(d: Date) {
  let _d = new Date(formatDate(d))
  return new Date(_d.setHours(0))
}

function setEndOfDay(d: Date) {
  let _d = setStartOfDay(d)
  _d = new Date(_d.setDate(_d.getDate() + 1))
  return new Date(_d.setMilliseconds(-1))
}

function localeDiffDate(d: Date) {
  const _d = new Date(d.getTime());
  return new Date(_d.setHours(_d.getHours() - diffHour));
}

const selectedDateTime = computed(() => {
  emit("update:modelValue", "");
  if (!selectedMinute.value) return;
  const _selectedDateTime = `${selectedDate.value} ${selectedHour.value}:${selectedMinute.value}`;
  emit("update:modelValue", _selectedDateTime);
  return _selectedDateTime;
});

function createDateList(start: Date | undefined, end: Date | undefined) {
  const ret: selectable[] = [];

  const startDay = new Date(start.getTime());
  const nextDay = new Date(startDay.getTime());
  nextDay.setDate(startDay.getDate() + 1);

  while (end >= startDay) {
    const formatted = formatDate(localeDiffDate(startDay));
    let selectable = false
    props.ranges.forEach((range: rangeType) => {
      selectable = selectable ||
        new Date(range.start_at).getTime() != new Date(range.end_at).getTime() &&
        range.start_at < nextDay &&
        range.end_at > startDay;
    })
    if (selectable) {
      ret.push({
        value: formatted,
        selectable: selectable,
        selected: selectedDate.value == formatted,
      });
    }
    startDay.setDate(startDay.getDate() + 1);
    nextDay.setDate(nextDay.getDate() + 1);
  }
  return ret.reverse();
}

function createHourList() {
  const ret: selectable[] = [];
  const creatable = !!selectedDate.value;

  let startTime = new Date(selectedDate.value as Date)
  startTime.setHours(startTime.getHours() + diffHour);
  let nextTime = new Date(startTime.getTime());
  nextTime.setHours(nextTime.getHours() + 1);

  for (let h = 0; h < 24; h++) {
    let selectable = false;

    props.ranges.forEach((range: rangeType) => {
      if (
        creatable &&
        new Date(range.start_at).getTime() != new Date(range.end_at).getTime() &&
        range.start_at < nextTime &&
        range.end_at > startTime
      ) {
        selectable = true;
        return;
      }
    });
    const formatted = String(h).padStart(2, '0');
    ret.push({
      value: formatted,
      selectable: selectable,
      selected: selectedHour.value == formatted,
    });
    startTime.setHours(startTime.getHours() + 1);
    nextTime.setHours(nextTime.getHours() + 1);
  }
  return ret;
}

function createMinuteList() {
  const ret: selectable[] = [];
  const creatable = !!selectedHour.value;

  let startTime = new Date(selectedDate.value as Date)
  startTime.setHours(startTime.getHours() + Number(selectedHour.value) + diffHour);
  let nextTime = new Date(startTime.getTime());
  nextTime.setMinutes(nextTime.getMinutes() + 1);

  for (let m = 0; m < 60; m++) {
    let selectable = false;
    props.ranges.forEach((range: rangeType) => {
      if (creatable && range.start_at < nextTime && range.end_at > startTime) {
        selectable = true;
        return;
      }
    });
    const formatted = String(m).padStart(2, '0');
    ret.push({
      value: formatted,
      selectable: selectable,
      selected: selectedMinute.value == formatted,
    });
    startTime.setMinutes(startTime.getMinutes() + 1);
    nextTime.setMinutes(nextTime.getMinutes() + 1);
  }
  return ret;
}

async function selectDate(select: selectable) {
  if (selectedDate.value !== select.value) {
    selectedHour.value = undefined;
    selectedMinute.value = undefined;
  }
  if (!select.selectable) return;

  selectedDate.value = select.value;
  await nextTick();
  scrollHour();
  scrollMinute();
}

async function selectHour(select: selectable) {
  if (selectedHour.value !== select.value) {
    selectedMinute.value = undefined;
  }
  if (!select.selectable) return;

  selectedHour.value = select.value;
  await nextTick();
  scrollMinute();
}

function selectMinute(select: selectable) {
  if (!select.selectable) return;
  selectedMinute.value = select.value;
}

function getListContentClass(data: selectable, index: number) {
  let ret = data.selectable ? "selectable selectable-odd-number" : "unselectable";
  if (data.selectable && index % 2 == 0) ret = "selectable selectable-even-number"
  if (data.selected) return "selected";
  return ret;
}

function scrollHour() {
  scrollTopSelectable("select-time-hour");
}

function scrollMinute() {
  scrollTopSelectable("select-time-minute");
}

function scrollTopSelectable(id: string) {
  const parent = document.getElementById(id);
  const target = parent?.getElementsByClassName("selectable")[0];
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth" });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("p", null, [
      _createElementVNode("b", null, _toDisplayString(__props.label) + ": " + _toDisplayString(selectedDateTime.value), 1)
    ]),
    _createElementVNode("div", _hoisted_1, [
      (__props.loading || props.reading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("div", { class: "not-searched" }, null, -1)
          ]))]))
        : (!__props.ranges[0] || !dates.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("label", null, _toDisplayString(_unref(i18n).global.t('pages.cvr_uploads.requestPanel.deliveryDoesNotExists')), 1)
              ])
            ]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dates.value, (date, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: date.value,
                    class: _normalizeClass(["list-element", getListContentClass(date, index)]),
                    onClick: ($event: any) => (selectDate(date))
                  }, _toDisplayString(date.value), 11, _hoisted_6))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hours.value, (hour, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: hour.value,
                    class: _normalizeClass(["list-element", getListContentClass(hour, index)]),
                    onClick: ($event: any) => (selectHour(hour))
                  }, _toDisplayString(hour.value) + _toDisplayString(_unref(i18n).global.t('pages.cvr_uploads.hour')), 11, _hoisted_8))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(minutes.value, (minute, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: minute.value,
                    class: _normalizeClass(["list-element", getListContentClass(minute, index)]),
                    onClick: ($event: any) => (selectMinute(minute))
                  }, _toDisplayString(minute.value) + _toDisplayString(_unref(i18n).global.t('pages.cvr_uploads.minute')), 11, _hoisted_10))
                }), 128))
              ])
            ], 64))
    ]),
    _createVNode(ValidationErrors, { messages: __props.errors }, null, 8, ["messages"])
  ], 64))
}
}

})